import { useTranslation } from 'react-i18next';

import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ensurePemCertificate, toBase64 } from 'helpers';

import { SignType } from 'enums';
import useRegisterApi from 'hooks/use-register';
import useRegisterStart from 'hooks/use-start-register';
import { useState } from 'react';
import { handleAuth } from 'store/reducers/auth.reducer';
import { selectCurrentCertificate, setSignLoading } from 'store/reducers/certificates';
import { useClient } from 'client/hooks/useClient';
import { AppRoutes } from 'config/routes';
import { organizationsApi } from 'store/api/organizations';
import { store } from 'store/store';

export default function useRegister() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);

  const { mutateAsync: initHandshake } = useRegisterStart();

  const { sign, signXml, client } = useClient();

  const { mutateAsync: confirmRegistration } = useRegisterApi();

  const certificate = useSelector(selectCurrentCertificate());

  const startRegister = async () => {
    try {
      if (!certificate) {
        return null;
      }

      setLoading(true);

      // signup/pki/start requires full PEM; mock stores Base64 DER without headers.
      const data = await initHandshake(ensurePemCertificate(certificate.Base64RawData));

      setLoading(false);

      return data;
    } catch (error: any) {
      message.error(error?.message as string);
      navigate(AppRoutes.Auth.Home + AppRoutes.Auth.Login + AppRoutes.Auth.Certificate);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const prepareOrganizationAfterSignup = async () => {
    const inn =
      certificate?.Subject?.['ИННЮЛ'] ||
      (certificate as any)?.ИННЮЛ ||
      certificate?.Subject?.['ИНН'] ||
      '';
    const ogrn = certificate?.Subject?.['ОГРН'] || (certificate as any)?.ОГРН || '';

    if (!inn && !ogrn) {
      return;
    }

    const organizationAttributes = [
      ...(inn ? [{ name: 'inn_legal_entity', value: inn }] : []),
      ...(ogrn ? [{ name: 'ogrn', value: ogrn }] : []),
    ];

    await store
      .dispatch(
        organizationsApi.endpoints.prepareOrganization.initiate({
          organizationAttributes,
        }),
      )
      .unwrap();
  };

  const register = async ({
    token,
    secret,
    tbsType,
  }: {
    token: string;
    secret: string;
    tbsType: string;
  }) => {
    try {
      // Without a certificate or a handshake result there is nothing to sign. Say so:
      // silently doing nothing here looks to the user like a dead "Sign" button.
      if (!certificate || !secret || !token) {
        message.error(t('Something went wrong...'));
        return;
      }

      dispatch(setSignLoading(true));
      let signature = '';
      let detachedSignature = '';

      if (tbsType === SignType.XMLDSIG) {
        const { signature: a, detachedSignature: b } = await signXml({
          certificate,
          dataForSign: secret,
        });
        signature = a;
        detachedSignature = b;
      } else {
        signature = await sign({ dataForSign: toBase64(secret), certificate });
      }

      const payload = {
        token,
        signature,
        detachedSignature,
      };

      try {
        const result = await confirmRegistration(payload);
        dispatch(handleAuth(result));
        try {
          await prepareOrganizationAfterSignup();
        } catch (prepareError: any) {
          // Tokens are already issued — surface prepare error but still enter LK.
          message.error(prepareError?.message || t('Something went wrong...'));
        }
        navigate(state?.from ?? AppRoutes.Dashboard.Home + AppRoutes.Dashboard.Contracts.Home);
      } catch (error: any) {
        if (error?.message) {
          message.error(error?.message as string);
          return;
        }
        throw t('Something went wrong...');
      }
    } catch (error: any) {
      if (error.message && error.message === 'register certificate first') {
        message.error(t('The organization is not connected'));
      } else {
        message.error(t('Something went wrong...'));
      }
    } finally {
      dispatch(setSignLoading(false));
    }
  };

  return { register, startRegister, isLoading, isClientLoading: !client };
}
